import { ReactNode, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWBEAuthSession, useWBELogout } from "../api/api-hooks/wbe-auth";
import type { WBEAuthAccount } from "../api/contracts/wbe-auth";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { useConfiguration } from "./ConfigurationProvider";
import { useHeader } from "./HeaderContext";
import { WBEAuthContext } from "./wbe-auth-context";
import { useOfferedLanguages } from "../utils/useOfferedLanguages";

export const WBEAuthProvider = ({ children }: { children: ReactNode }) => {
  const { configuration } = useConfiguration();
  const { applySelectedLanguage, selectedLanguage } = useHeader();
  const offeredLanguages = useOfferedLanguages();
  const queryClient = useQueryClient();
  const logoutMutation = useWBELogout();
  const [hasSessionCookie, setHasSessionCookie] = useState(true);
  const session = useWBEAuthSession({
    variables: { siteId: configuration?.siteId ?? "" },
  });

  const sessionData = session.data;
  const account = sessionData?.authenticated ? sessionData.account : null;

  useEffect(() => {
    if (
      configuration?.siteId &&
      sessionData?.authenticated === false &&
      hasSessionCookie
    ) {
      setHasSessionCookie(false);
    }
  }, [configuration?.siteId, hasSessionCookie, sessionData]);

  /**
   * The account carries a language of its own, which also decides what its
   * e-mails are written in. It only takes over the session when this site
   * still offers it: a guest whose account says German on a site that has
   * switched German off keeps browsing in a language the site serves, while
   * the account keeps its own for e-mail.
   */
  const accountLanguage =
    account?.languageCode && offeredLanguages.includes(account.languageCode)
      ? account.languageCode
      : null;

  useEffect(() => {
    if (accountLanguage) applySelectedLanguage(accountLanguage);
  }, [accountLanguage, applySelectedLanguage]);

  const loginWithSession = (nextAccount: WBEAuthAccount, expiresAt: string) => {
    setHasSessionCookie(true);
    if (nextAccount.languageCode) applySelectedLanguage(nextAccount.languageCode);
    queryClient.setQueryData(
      ["useWBEAuthSession", { siteId: configuration?.siteId ?? "" }],
      {
        authenticated: true,
        account: nextAccount,
        expiresAt,
      },
    );
  };

  const clearLocalSession = () => {
    setHasSessionCookie(false);
    queryClient.setQueriesData(
      { queryKey: ["useWBEAuthSession"] },
      { authenticated: false },
    );
  };

  const logout = async () => {
    try {
      if (configuration?.siteId) {
        await logoutMutation.mutateAsync({ siteId: configuration.siteId });
      }
    } finally {
      clearLocalSession();
    }
  };

  // Held only while a language the site actually serves is being applied;
  // waiting on one it does not would never finish.
  const isApplyingAccountLanguage =
    !!accountLanguage && selectedLanguage !== accountLanguage;

  if (isApplyingAccountLanguage) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <WBEAuthContext.Provider
      value={{
        account,
        isAuthenticated: Boolean(account),
        isLoading:
          hasSessionCookie && (!configuration?.siteId || session.isLoading),
        loginWithSession,
        logout,
      }}
    >
      {children}
    </WBEAuthContext.Provider>
  );
};
